import { BookOpen, Play } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CRITTERS } from "../data/dolopals";
import { CritterShape, critterCry, critterOfTheWeek, isShinySunday } from "./TitanExpeditions";

// brightest neon spread — every shape family represented
const SHOWCASE = [
  "bolt-toad",
  "static-ferret",
  "signal-moth",
  "neon-newt",
  "volt-vole",
  "echo-bat",
  "wire-snake",
  "dust-bunny",
  "spark-snail",
  "siren-squid",
  "static-starfish",
  "ember-crow",
];
// dark silhouettes tease the hunt, just like the real book
const HIDDEN = ["gasmask-gull", "rust-scorpion"];

const byId = (id) => CRITTERS.find((c) => c.id === id);

const readBookCount = () => {
  try {
    const e = JSON.parse(localStorage.getItem("dolopals-expeditions"));
    return e && Array.isArray(e.critters) ? e.critters.length : 0;
  } catch {
    return 0;
  }
};

/* Neon critter showcase banner. Full grid on the home page (tiles link to KidZone);
   compact single row on the DoloPals page (CTA scrolls down to the real book).
   Tiles flicker alive one at a time like a radar sweep, sing their species call on
   hover/tap, and one tile turns gold on Shiny Sundays. */
export function CritterBanner({ compact = false }) {
  const [found] = useState(readBookCount);
  const lastCryRef = useRef(0);
  // CRITTER OF THE WEEK — the spotlight species always makes the showcase row
  const weekly = critterOfTheWeek();
  const showcase = (compact ? SHOWCASE.slice(0, 6) : SHOWCASE).map(byId).filter(Boolean);
  if (!showcase.some((c) => c.id === weekly.id)) showcase[showcase.length - 1] = weekly;
  const hidden = (compact ? HIDDEN.slice(0, 1) : HIDDEN).map(byId).filter((c) => c && c.id !== weekly.id);
  // SHINY SUNDAY — one showcase tile gleams gold all day, rotating weekly
  const shinyIdx = isShinySunday() ? Math.floor(Date.now() / 604800000) % showcase.length : -1;

  // species cry on hover/tap — throttled so sweeping across the grid stays musical
  const cry = (id) => {
    const now = Date.now();
    if (now - lastCryRef.current < 350) return;
    lastCryRef.current = now;
    critterCry(id);
  };

  const scrollToBook = () => {
    document.querySelector('[data-testid="critter-book"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // flips the book into match mode (CritterBook listens) and scrolls to it
  const scrollToMatch = () => {
    window.dispatchEvent(new CustomEvent("dolo-play-match"));
    scrollToBook();
  };

  const renderTile = (c, i) => {
    const isTeaser = i === shinyIdx;
    const isWeekly = c.id === weekly.id;
    const color = isTeaser ? "#ffd700" : c.color;
    const label =
      isTeaser && isWeekly ? "★ SHINY OF THE WEEK" : isTeaser ? "★ SHINY TODAY" : isWeekly ? "OF THE WEEK" : null;
    const inner = (
      <>
        <CritterShape shape={c.shape} color={color} size={54} />
        <strong>{c.name}</strong>
        {label ? <em>{label}</em> : null}
      </>
    );
    const props = {
      className: `critter-banner-tile${isTeaser ? " is-shiny-teaser" : ""}${isWeekly ? " is-weekly" : ""}`,
      style: { "--cc": color, "--i": i },
      "data-testid": `critter-banner-tile-${c.id}`,
      onPointerEnter: () => cry(c.id),
    };
    return compact ? (
      <span key={c.id} {...props} onClick={() => cry(c.id)}>
        {inner}
      </span>
    ) : (
      <Link key={c.id} to="/dolopals" {...props}>
        {inner}
      </Link>
    );
  };

  return (
    <section className={`critter-banner ${compact ? "is-compact" : ""}`} data-testid="critter-banner">
      <div className="critter-banner-head">
        <div className="critter-banner-copy">
          <p className="eyebrow">FIELD GUIDE // SCRAP CRITTERS</p>
          <h2>THE CRITTER BOOK.</h2>
          <p>
            50 little scavengers roam the Titan Tracker grid. Every one that wanders in moves into your book — fill
            the grid to discover them all.
          </p>
          {found > 0 ? (
            <span className="critter-banner-progress" data-testid="critter-banner-progress">
              YOUR BOOK — {found}/{CRITTERS.length} FOUND
            </span>
          ) : null}
        </div>
        <div className="critter-banner-ctas">
          {compact ? (
            <>
              <button
                type="button"
                className="critter-banner-cta is-secondary"
                onClick={scrollToBook}
                data-testid="critter-banner-book-cta"
              >
                <BookOpen size={15} /> CRITTER BOOK
              </button>
              <button type="button" className="critter-banner-cta" onClick={scrollToMatch} data-testid="critter-banner-match-cta">
                <Play size={15} /> PLAY CRITTER MATCHING GAME
              </button>
            </>
          ) : (
            <>
              <Link to="/dolopals" className="critter-banner-cta is-secondary" data-testid="critter-banner-book-cta">
                <BookOpen size={15} /> CRITTER BOOK
              </Link>
              <Link to="/dolopals?play=match" className="critter-banner-cta" data-testid="critter-banner-match-cta">
                <Play size={15} /> PLAY CRITTER MATCHING GAME
              </Link>
            </>
          )}
        </div>
      </div>
      <div className="critter-banner-grid">
        {showcase.map(renderTile)}
        {hidden.map((c) => (
          <span key={c.id} className="critter-banner-tile is-hidden" data-testid={`critter-banner-tile-${c.id}`}>
            <CritterShape shape={c.shape} color="#242a31" size={54} />
            <strong>???</strong>
            <em>STILL HIDING</em>
          </span>
        ))}
      </div>
    </section>
  );
}
